import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { saveAuthTokens } from "../../../../../utils/authSession.js";

import { getApi, postApi, ApiError } from "../../../../utils/api.ts";
import { SIGN_IN, USER_PROFILE } from "../../../../../config/Endpoints.js";
import { clearManagerStatus, fetchManagerStatus, writeManagerStatus } from "../../../../hooks/manager-status.ts";
import type { LoginFormValues } from "../schemas/index.ts";

interface SignInResponse {
  accessToken: string;
  refreshToken: string;
  user: Record<string, unknown> & { photoPath?: string | null };
}

interface UserProfileResponse {
  data: {
    personalDetail?: { photoPath?: string | null } | null;
  };
}

interface UseLoginReturn {
  loading: boolean;
  error: string | null;
  submit: (values: LoginFormValues) => Promise<void>;
}

const FORGOT_PASSWORD_REDIRECT_DELAY_MS = 4000;
const ERROR_AUTO_CLEAR_MS = 4000;

export function useLogin(): UseLoginReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const submit = useCallback(
    async (values: LoginFormValues) => {
      setLoading(true);
      setError(null);
      try {
        const { data: signInRes } = await postApi<SignInResponse>(
          SIGN_IN,
          undefined,
          { email: values.email, password: values.password },
        );

        saveAuthTokens(signInRes);

        let photoPath: string | null | undefined;
        try {
          const { data: profileRes } = await getApi<UserProfileResponse>(
            USER_PROFILE,
            signInRes.accessToken,
          );
          photoPath = profileRes.data?.personalDetail?.photoPath ?? null;
        } catch {
          photoPath = null;
        }

        const userData = { ...signInRes.user, photoPath };
        localStorage.setItem("userData", JSON.stringify(userData));

        const userId = typeof userData.id === "number" || typeof userData.id === "string"
          ? userData.id
          : typeof userData.email === "string" ? userData.email : null;
        if (userId != null) {
          try {
            const managerStatus = await fetchManagerStatus(signInRes.accessToken);
            writeManagerStatus(userId, managerStatus);
          } catch {
            // Manager status is refreshed again when a page using it opens.
            clearManagerStatus(userId);
          }
        }

        navigate("/dashboard/");
      } catch (e) {
        const message =
          e instanceof Error && e.message
            ? e.message
            : "Unable to sign in. Please try again.";
        setError(message);

        if (e instanceof ApiError && e.status === 403) {
          window.setTimeout(() => {
            setError(null);
            navigate("/forgot-password");
          }, FORGOT_PASSWORD_REDIRECT_DELAY_MS);
        } else {
          window.setTimeout(() => setError(null), ERROR_AUTO_CLEAR_MS);
        }
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  return { loading, error, submit };
}
